import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  HostListener,
  Input,
  OnDestroy,
  ViewContainerRef,
  createComponent,
} from '@angular/core';
import { TooltipComponent } from '../component/tooltip/tooltip.component';

export enum TooltipPosition {
  ABOVE = 'above',
  BELOW = 'below',
  LEFT = 'left',
  RIGHT = 'right',
  DYNAMIC = 'dynamic',
  DEFAULT = 'above',
}

@Directive({
    selector: '[appTooltip]',
    standalone: false
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') tooltipText: string;
  // Allow binding the enum or its string-literal value (templates use e.g. tooltipPosition="above").
  @Input() tooltipPosition: TooltipPosition | `${TooltipPosition}` = TooltipPosition.DEFAULT;
  @Input() showDelay = 0;
  @Input() hideDelay = 0;
  @Input() tooltipDisabled: boolean = false;

  private componentRef: ComponentRef<TooltipComponent> | null = null;
  private hideTimeout?: number;
  private showTimeout?: number;
  private touchTimeout?: number;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
    private viewContainerRef: ViewContainerRef,
    private environmentInjector: EnvironmentInjector
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltipDisabled) this.initializeTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.tooltipDisabled) this.setHideTooltipTimeout();
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove($event: MouseEvent): void {
    if (this.componentRef !== null && this.tooltipPosition === 'dynamic') {
      this.componentRef.instance.left = $event.clientX;
      this.componentRef.instance.top = $event.clientY;
      this.componentRef.instance.tooltip = this.tooltipText;
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart($event: TouchEvent): void {
    window.clearTimeout(this.touchTimeout);
    this.touchTimeout = window.setTimeout(this.initializeTooltip.bind(this), 500);
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    window.clearTimeout(this.touchTimeout);
    this.setHideTooltipTimeout();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    window.clearTimeout(this.touchTimeout);
    this.setHideTooltipTimeout();
  }

  private initializeTooltip() {
    if (this.componentRef === null) {
      window.clearInterval(this.hideDelay);
      this.componentRef = createComponent(TooltipComponent, {
        environmentInjector: this.environmentInjector,
      });

      this.appRef.attachView(this.componentRef.hostView);
      const [tooltipDOMElement] = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes;

      this.setTooltipComponentProperties();

      document.body.appendChild(tooltipDOMElement);
      this.showTimeout = window.setTimeout(this.showTooltip.bind(this), this.showDelay);
    }
  }

  private setTooltipComponentProperties() {
    if (this.componentRef !== null) {
      this.viewContainerRef.clear();

      this.componentRef.instance.tooltip = this.tooltipText;
      this.componentRef.instance.position = this.tooltipPosition;

      const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();

      let componentLeft, componentTop;
      switch (this.tooltipPosition) {
        case TooltipPosition.BELOW:
          componentLeft = Math.round((right - left) / 2 + left);
          componentTop = Math.round(bottom);
          break;

        case TooltipPosition.ABOVE:
          componentLeft = Math.round((right - left) / 2 + left);
          componentTop = Math.round(top);
          break;

        case TooltipPosition.RIGHT:
          componentLeft = Math.round(right);
          componentTop = Math.round(top + (bottom - top) / 2);
          break;

        case TooltipPosition.LEFT:
          componentLeft = Math.round(left);
          componentTop = Math.round(top + (bottom - top) / 2);
          break;

        default:
          break;
      }

      this.componentRef.instance.left = componentLeft;
      this.componentRef.instance.top = componentTop;
    }
  }

  private showTooltip() {
    if (this.componentRef !== null && !this.tooltipDisabled) {
      this.componentRef.instance.visible = true;
    }
  }

  private setHideTooltipTimeout() {
    this.hideTimeout = window.setTimeout(this.destroy.bind(this), this.hideDelay);
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(): void {
    if (this.componentRef !== null) {
      this.viewContainerRef?.clear();
      window.clearInterval(this.showTimeout);
      window.clearInterval(this.hideDelay);
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
